import { DynamicDrawUsage, InstancedMesh, Matrix4, Vector3, type Material } from 'three';
import { hexToLinear } from '../core/color';
import type { Vec3 } from '../geo/types';
import { FLAME_COLOUR } from '../render/night/nightLights';
import { createMeshBuilder } from '../world/mesh/meshBuilder';
import { rounded } from '../world/life/lifeGeometry';
import { streetInstances } from '../world/life/streetAsset';
import { skinnedPoint } from '../world/life/citizens/citizenPose';
import type { CitizenAsset } from '../world/life/citizens/citizenAsset';
import type { NightLight, NightLightSource } from '../world/types';

/*
 * CitizenRig's left wrist plus 6.5 cm along the rest hand, converted to the baked
 * +X frame, keeps the saucer centred on the palm rather than beside the sleeve.
 */
export const CANDLE_GRIP: Vec3 = [0, 0.9742716, -0.5857284];
export const CANDLE_FLAME: Vec3 = [0.02, 0.265, 0];

export function buildVisitorCandle() {
  const builder = createMeshBuilder();
  rounded(builder, hexToLinear('#766448'), [
    { y: 0.028, x: 0.02, depth: 0.052, half: 0.052 },
    { y: 0.04, x: 0.02, depth: 0.079, half: 0.079 },
    { y: 0.052, x: 0.02, depth: 0.079, half: 0.079 },
  ]);
  rounded(builder, hexToLinear('#d8c7a0'), [
    { y: 0.052, x: 0.02, depth: 0.023, half: 0.023 },
    { y: 0.225, x: 0.02, depth: 0.02, half: 0.02 },
    { y: 0.232, x: 0.02, depth: 0.018, half: 0.018 },
  ]);
  rounded(
    builder,
    hexToLinear('#3d3528'),
    [
      { y: 0.232, x: 0.02, depth: 0.003, half: 0.003 },
      { y: 0.249, x: 0.02, depth: 0.003, half: 0.003 },
    ],
    4
  );
  return builder.finish();
}

export function candleGripAt(
  asset: CitizenAsset,
  matrix: Matrix4,
  pose: { readonly from: number; readonly to: number; readonly mix: number }
): Vec3 {
  const joint = asset.bones.indexOf('hand.L');
  if (joint < 0) throw new Error('Visitor candle requires the citizen left hand');
  const point = skinnedPoint(asset, joint, CANDLE_GRIP, pose.from, pose.to, pose.mix);
  const world = new Vector3(...point).applyMatrix4(matrix);
  return [world.x, world.y, world.z];
}

export function createVisitorCandles(asset: CitizenAsset, material: Material, capacity: number) {
  const geometry = buildVisitorCandle();
  streetInstances(geometry, capacity, 1980);
  const mesh = new InstancedMesh(geometry, material, capacity);
  mesh.name = 'visitor-candles';
  mesh.frustumCulled = false;
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  mesh.count = 0;
  const lights: NightLight[] = [];
  const matrix = new Matrix4();
  const source: NightLightSource = { moving: true, list: () => lights };
  return {
    mesh,
    lights: source,
    begin(): void {
      mesh.count = 0;
      lights.length = 0;
    },
    draw(body: Matrix4, pose: Parameters<typeof candleGripAt>[2], phase: number): void {
      if (mesh.count >= capacity) throw new Error('Visitor candle capacity exceeded');
      const grip = candleGripAt(asset, body, pose);
      matrix.makeTranslation(...grip);
      mesh.setMatrixAt(mesh.count++, matrix);
      lights.push({
        position: [grip[0] + CANDLE_FLAME[0], grip[1] + CANDLE_FLAME[1], grip[2] + CANDLE_FLAME[2]],
        colour: FLAME_COLOUR,
        reach: 5.5,
        size: 0.07,
        phase,
      });
    },
    end(): void {
      mesh.visible = mesh.count > 0;
      mesh.instanceMatrix.needsUpdate = true;
    },
    dispose(): void {
      lights.length = 0;
      geometry.dispose();
      mesh.dispose();
    },
  };
}
